import { Observable, Subscription, filter, map, take } from 'rxjs';
import { EventEmitter, Input, OnDestroy, Output, Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';
import { ConsumerPortalConfig, Menu } from '../../models/consumer-portal-config';
import { ConfigurationService } from '../../services/configuration.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '../../services/logger.service';
import { ComponentsToLoad } from '../../enums/components-to-load.enum';
import { GapiLicense } from '../../enums/gapi-license';
import { Navigation } from '../../models/navigation.model';
import { AppMediatorService } from '../../services/app-mediator.service';
import { AppFacadeService } from '../../services/app-facade.service';
import { PathRoutingService } from '../../services/path-routing.service';
import { UserAccountFeature } from '../../enums/user-account-feature.enum';

@Component({
    standalone: false,
    selector: 'por-page-sidebar',
    templateUrl: './page-sidebar.component.html',
    styleUrls: ['./page-sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageSidebarComponent implements OnInit, OnDestroy {
    private readonly configService = inject(ConfigurationService);
    private readonly translateService = inject(TranslateService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly logger = inject(LoggerService);
    readonly appFacadeService = inject(AppFacadeService);

    @Output() readonly getActiveTab = new EventEmitter();
    @Output() readonly logout = new EventEmitter();
    inputs: ConsumerPortalConfig | undefined;
    @Input() config!: string;
    menu!: Menu;
    consumerPortalWidth!: number;
    contractId: string | undefined;
    navigations$!: Observable<Navigation[]>;

    subscriptions: Subscription[] = [];
    customerId!: string;

    ngOnDestroy(): void {
        this.subscriptions.map(sub => sub.unsubscribe());
    }

    ngOnInit(): void {
        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }
        this.configService.setMatIconFontFamily();
        if (this.config && !isEmpty(this.config)) {
            this.inputs = JSON.parse(this.config);
            const width = this.inputs?.consumerPortal?.width as string;
            if (width) {
                this.consumerPortalWidth = Number(width.replace('px', ''));
            }

            this.logger.logAssert(this.inputs?.customerId, 'customerId is not defined');
            this.customerId = (this.configService.config?.customerId as string) || '0';
            this.menu = this.configService.config?.menu as Menu;
            // Wait until the user-accounts/roles endpoint has finished loading before building the menu, otherwise
            // feature checks default to available and inaccessible menu items are shown briefly.
            this.navigations$ = this.appFacadeService.isAccountFeaturesLoaded().pipe(
                filter(Boolean),
                map(() => this.buildNavigations())
            );
        }

        if (this.inputs?.contractId) {
            this.contractId = this.inputs?.contractId;
        }

        // Restore the active tab from a plain '#{tab}' hash on load (advance-only by virtue of
        // where the sidebar mounts). Deferred until account features load and gated on the tab's
        // per-account permission, so a '#{tab}' to a revoked tab cannot re-enter it via the URL.
        // hashRoute returns null for '#{tab}/ContractDetail/{id}' deep links, so this does not
        // clobber the contract deep link handled by CustomerPortalComponent.
        // Interim: full decouple of inbound routing from the sidebar is tracked in the follow-on tech-debt ticket.
        const hashRoute = PathRoutingService.hashRoute;
        if (hashRoute) {
            this.subscriptions.push(
                this.appFacadeService
                    .isAccountFeaturesLoaded()
                    .pipe(filter(Boolean), take(1))
                    .subscribe(() => {
                        if (this.isHashTabPermitted(hashRoute.tab)) {
                            this.appFacadeService.setActiveTab(hashRoute.tab);
                        }
                    })
            );
        }
    }

    /**
     * Per-account permission gate for a plain '#{tab}' restore. Mirrors the account-feature
     * checks buildNavigations uses to hide the tab buttons. Tabs with no account gate pass through.
     */
    private isHashTabPermitted(tab: ComponentsToLoad): boolean {
        switch (tab) {
            case ComponentsToLoad.Contracts:
                return this.appFacadeService.featureToggleService.isAvailable(UserAccountFeature.ViewContracts);
            case ComponentsToLoad.Invoices:
                return this.appFacadeService.featureToggleService.isAvailable(UserAccountFeature.ViewInvoices);
            case ComponentsToLoad.ItemsOut:
                return this.appFacadeService.featureToggleService.isAvailable(UserAccountFeature.ViewItemsOnRent);
            default:
                return true;
        }
    }

    private buildNavigations(): Navigation[] {
        const navigations: Navigation[] = [];
        [
            this.appFacadeService.versionToggleService.isAdvanceVersion() && {
                tab: ComponentsToLoad.AccountSummary,
                text: this.translateService.instant('Home'),
                params: []
            },
            this.appFacadeService.featureToggleService.isAvailable('Account.ViewContracts') && {
                tab: ComponentsToLoad.Contracts,
                text: this.translateService.instant('Contracts'),
                params: []
            },
            this.appFacadeService.versionToggleService.isAdvanceVersion() &&
                this.appFacadeService.featureToggleService.isAvailable('invoices') &&
                this.appFacadeService.featureToggleService.isAvailable('Account.ViewInvoices') && {
                    tab: ComponentsToLoad.Invoices,
                    text: this.translateService.instant('Invoices'),
                    params: []
                },
            this.appFacadeService.featureToggleService.isAvailable('Account.ViewItemsOnRent') &&
                this.appFacadeService.versionToggleService.isAdvanceVersion() && {
                    tab: ComponentsToLoad.ItemsOut,
                    text: this.translateService.instant('Items Out'),
                    params: []
                },
            this.appFacadeService.featureToggleService.isAvailable('canSeeTrackingTab') &&
                this.appFacadeService.featureToggleService.isAvailable('trackingTab') &&
                this.appFacadeService.versionToggleService.hasLicense(GapiLicense.ConsumerPortalTracking) && {
                    tab: ComponentsToLoad.Tracking,
                    text: this.translateService.instant('Tracking'),
                    params: []
                }
        ].map(nav => {
            if (nav) {
                const tab: string = nav.tab.toString();
                if (this.menu && Object.prototype.hasOwnProperty.call(this.menu, tab)) {
                    navigations.push(nav);
                }
                if (!this.menu) {
                    navigations.push(nav);
                }
            }
        });
        return navigations;
    }

    toggleTab(tab: ComponentsToLoad | undefined) {
        if (tab) {
            // if contracts tab is selected and routeTo (not embedded) is not defined, then clear the selected contractId
            const clearSelectedContractId = (!this.inputs?.routeTo?.route && tab === ComponentsToLoad.Contracts) || tab !== ComponentsToLoad.Contracts;
            this.appFacadeService.setActiveTab(tab, clearSelectedContractId);
            this.getActiveTab.emit(tab);
        }
    }
}
